// Aurelion Sol — a cosmic meteor crashes from the top of the screen into the
// enemy team, preceded by a warning shadow ellipse.
// Phases: SHADOW (warning ellipse) → FALL (meteor drops) → IMPACT (explosion)

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const SHADOW_MS = 520;
const FALL_MS   = 340;
const IMPACT_MS = 520;
const TOTAL     = SHADOW_MS + FALL_MS + IMPACT_MS;

const METEOR_R = 28;

// Debris: angle, speed, size, color
const DEBRIS = Array.from({ length: 18 }, (_, i) => ({
  angle: (i / 18) * Math.PI * 2 + i * 0.15,
  speed: 0.5 + (i % 4) * 0.22,
  size:  3 + (i % 5),
  color: i % 3 === 0 ? "#ffaa33" : i % 3 === 1 ? "#cc6600" : "#884400",
}));

// Fire sparks radiating from impact
const SPARKS = Array.from({ length: 14 }, (_, i) => ({
  angle: (i / 14) * Math.PI * 2,
  speed: 0.7 + (i % 3) * 0.18,
}));

export class AurelionMeteorVFX implements Animation {
  private elapsed    = 0;
  private meteorStart: Point;

  constructor(
    private landing: Point,      // where the meteor hits
    private ellipseRX: number,   // warning ellipse half-width
    private ellipseRY: number,   // warning ellipse half-height
    private onImpact?: () => void,
  ) {
    // Meteor starts 420px above landing
    this.meteorStart = { x: landing.x - 40, y: landing.y - 420 };
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < SHADOW_MS + FALL_MS && this.elapsed >= SHADOW_MS + FALL_MS) {
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;

    if (e < SHADOW_MS) {
      this.drawShadow(ctx, e / SHADOW_MS, 1);
    } else if (e < SHADOW_MS + FALL_MS) {
      const ft = (e - SHADOW_MS) / FALL_MS;
      this.drawShadow(ctx, 1, 1 - ft * 0.4);
      this.drawMeteor(ctx, ft);
    } else {
      this.drawImpact(ctx, (e - SHADOW_MS - FALL_MS) / IMPACT_MS);
    }
  }

  private drawShadow(ctx: CanvasRenderingContext2D, t: number, alpha: number): void {
    const pulse   = 0.55 + 0.45 * Math.sin(t * Math.PI * 7);
    const urgency = Easing.easeInQuad(t);
    const rx = this.ellipseRX * Easing.easeOutQuad(t);
    const ry = this.ellipseRY * Easing.easeOutQuad(t);
    const cx = this.landing.x;
    const cy = this.landing.y;

    ctx.save();

    // Dark shadow fill
    ctx.globalAlpha = alpha * urgency * 0.5 * pulse;
    ctx.fillStyle   = "#110800";
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();

    // Outer warning ring
    ctx.globalAlpha = alpha * (0.3 + 0.55 * urgency) * pulse;
    ctx.strokeStyle = "#ffaa33";
    ctx.lineWidth   = 2.5 + urgency * 2;
    ctx.shadowColor = "#cc6600";
    ctx.shadowBlur  = 14 + urgency * 18;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner ring (cosmic azure)
    ctx.globalAlpha = alpha * (0.2 + 0.3 * urgency) * pulse;
    ctx.strokeStyle = "#aaddff";
    ctx.lineWidth   = 1.5;
    ctx.shadowColor = "#4488ff";
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx * 0.5, ry * 0.5, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Danger lines — 4 lines crossing through center
    if (t > 0.4) {
      const lineAlpha = ((t - 0.4) / 0.6) * pulse;
      ctx.globalAlpha = alpha * lineAlpha * 0.45;
      ctx.strokeStyle = "#ffcc66";
      ctx.lineWidth   = 1;
      ctx.shadowBlur  = 4;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
        ctx.lineTo(cx - Math.cos(a) * rx, cy - Math.sin(a) * ry);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private drawMeteor(ctx: CanvasRenderingContext2D, t: number): void {
    const eased = Easing.easeInQuad(t);
    const mx = this.meteorStart.x + (this.landing.x - this.meteorStart.x) * eased;
    const my = this.meteorStart.y + (this.landing.y - this.meteorStart.y) * eased;
    const angle = Math.atan2(
      this.landing.y - this.meteorStart.y,
      this.landing.x - this.meteorStart.x,
    );
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.save();

    // Fire tail — gradient streak behind the meteor
    const tailLen = 120 + 80 * eased;
    const tailGrad = ctx.createLinearGradient(
      mx - cos * tailLen, my - sin * tailLen,
      mx, my,
    );
    tailGrad.addColorStop(0, "rgba(255,100,0,0)");
    tailGrad.addColorStop(0.3, "rgba(255,140,20,0.15)");
    tailGrad.addColorStop(0.7, "rgba(255,170,50,0.55)");
    tailGrad.addColorStop(1,   "rgba(255,220,100,0.9)");

    ctx.globalAlpha = 0.85;
    ctx.strokeStyle = tailGrad;
    ctx.lineWidth   = METEOR_R * 1.8;
    ctx.lineCap     = "round";
    ctx.shadowColor = "#ff6600";
    ctx.shadowBlur  = 28;
    ctx.beginPath();
    ctx.moveTo(mx - cos * tailLen, my - sin * tailLen);
    ctx.lineTo(mx, my);
    ctx.stroke();

    // Bright white-hot tip of the tail
    ctx.lineWidth   = METEOR_R * 0.6;
    ctx.strokeStyle = "#ffffee";
    ctx.shadowColor = "#ffffff";
    ctx.shadowBlur  = 16;
    ctx.beginPath();
    ctx.moveTo(mx - cos * tailLen * 0.35, my - sin * tailLen * 0.35);
    ctx.lineTo(mx, my);
    ctx.stroke();

    // Fire particle wisps along the trail
    for (let i = 0; i < 8; i++) {
      const f = i / 8;
      const wx = mx - cos * tailLen * f;
      const wy = my - sin * tailLen * f;
      const perp = { x: -sin, y: cos };
      const side = (i % 2 === 0 ? 1 : -1) * (3 + i * 1.5);
      ctx.globalAlpha = (1 - f) * 0.5;
      ctx.fillStyle = i % 2 === 0 ? "#ff8800" : "#ffbb44";
      ctx.shadowColor = "#ff6600";
      ctx.shadowBlur  = 10;
      ctx.beginPath();
      ctx.arc(wx + perp.x * side, wy + perp.y * side, 3 + f * 4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Meteor rocky body
    ctx.globalAlpha = 1;
    ctx.shadowColor = "#ffaa33";
    ctx.shadowBlur  = 30;
    const bodyGrad = ctx.createRadialGradient(mx - 6, my - 8, 2, mx, my, METEOR_R);
    bodyGrad.addColorStop(0,    "#ffffff");
    bodyGrad.addColorStop(0.18, "#ffee99");
    bodyGrad.addColorStop(0.42, "#dd8800");
    bodyGrad.addColorStop(0.7,  "#7a3800");
    bodyGrad.addColorStop(1,    "#2a0f00");
    ctx.fillStyle = bodyGrad;

    // Irregular rock shape
    ctx.beginPath();
    const ROCK_PTS = 10;
    const irregularity = [0.88, 1.05, 0.92, 1.1, 0.85, 0.97, 1.08, 0.90, 1.02, 0.86];
    for (let i = 0; i < ROCK_PTS; i++) {
      const a = (i / ROCK_PTS) * Math.PI * 2;
      const r = METEOR_R * irregularity[i];
      const px = mx + Math.cos(a) * r;
      const py = my + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    // Surface cracks on rock
    ctx.globalAlpha = 0.65;
    ctx.strokeStyle = "#ff6600";
    ctx.lineWidth   = 1.5;
    ctx.shadowColor = "#ff4400";
    ctx.shadowBlur  = 8;
    const cracks = [
      [-0.4, -0.6,  0.5, -0.1],
      [ 0.2,  0.5, -0.5,  0.6],
      [-0.1, -0.2,  0.6,  0.4],
    ];
    for (const [x1, y1, x2, y2] of cracks) {
      ctx.beginPath();
      ctx.moveTo(mx + x1 * METEOR_R, my + y1 * METEOR_R);
      ctx.lineTo(mx + x2 * METEOR_R, my + y2 * METEOR_R);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const fade  = 1 - Easing.easeInQuad(t);
    const cx    = this.landing.x;
    const cy    = this.landing.y;

    ctx.save();

    // Ground flash (only at start)
    if (t < 0.18) {
      ctx.globalAlpha = (1 - t / 0.18) * 0.7;
      ctx.fillStyle   = "#ffeecc";
      ctx.shadowColor = "#ffaa44";
      ctx.shadowBlur  = 40;
      ctx.beginPath();
      ctx.ellipse(cx, cy, this.ellipseRX * 1.5, this.ellipseRY * 1.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Main shockwave ring
    const ring1 = METEOR_R + t * this.ellipseRX * 2.8;
    ctx.globalAlpha = fade * 0.8;
    ctx.strokeStyle = "#ffaa33";
    ctx.lineWidth   = 4 - t * 2.5;
    ctx.shadowColor = "#cc6600";
    ctx.shadowBlur  = 22;
    ctx.beginPath();
    ctx.ellipse(cx, cy, ring1, ring1 * 0.42, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary cosmic ring
    const ring2 = METEOR_R + t * this.ellipseRX * 1.8;
    ctx.globalAlpha = fade * 0.5;
    ctx.strokeStyle = "#aaddff";
    ctx.lineWidth   = 2;
    ctx.shadowColor = "#4488ff";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.ellipse(cx, cy, ring2, ring2 * 0.4, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Debris flying out
    if (t < 0.6) {
      const debrisT = t / 0.6;
      for (const d of DEBRIS) {
        const dist = METEOR_R * 0.4 + this.ellipseRX * 2.5 * Easing.easeOutQuad(debrisT) * d.speed;
        const px = cx + Math.cos(d.angle) * dist;
        const py = cy + Math.sin(d.angle) * dist * 0.4;
        ctx.globalAlpha = fade * (1 - debrisT * 0.5) * 0.85;
        ctx.fillStyle   = d.color;
        ctx.shadowColor = "#cc6600";
        ctx.shadowBlur  = 6;
        ctx.beginPath();
        ctx.arc(px, py, d.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Fire sparks
    if (t < 0.5) {
      const sparkT = t / 0.5;
      for (const sp of SPARKS) {
        const dist = 10 + 55 * Easing.easeOutQuad(sparkT) * sp.speed;
        const px = cx + Math.cos(sp.angle) * dist;
        const py = cy + Math.sin(sp.angle) * dist * 0.45;
        ctx.globalAlpha = fade * (1 - sparkT) * 0.8;
        ctx.strokeStyle = "#ffcc44";
        ctx.lineWidth   = 2;
        ctx.shadowColor = "#ff8800";
        ctx.shadowBlur  = 8;
        const innerPx = cx + Math.cos(sp.angle) * (dist - 12);
        const innerPy = cy + Math.sin(sp.angle) * (dist - 12) * 0.45;
        ctx.beginPath();
        ctx.moveTo(innerPx, innerPy);
        ctx.lineTo(px, py);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
